import {toPoint} from '@/utils/polyline'
import {locationLabel} from '@/utils/functions'

/**
 * THE MANIFEST — one ride, re-read as a delivery run.
 *
 * ── Why this exists ───────────────────────────────────────────────────────────────
 * `/trips/:id/enroute` answers ONE question: how do I reach the passenger. It solves a
 * single leg (driver → pickup) and knows nothing about what comes after it. That is the
 * right shape for a hail, and the wrong shape for a multi-stop run.
 *
 * `/trips/:id/enroute/new` is the logistics reading of the same trip: every waypoint —
 * pickup, each stop-over, the drop-off — is a DROP on a manifest, the driver works down
 * the list in order, and the whole remaining run is routed in ONE Directions call so that
 * every drop carries its own ETA rather than only the next one. That is the difference
 * between "where am I going" and "when does this run finish", and a dispatcher-facing
 * screen needs the second.
 *
 * ── ⚠️ `waypoint.location` IS AN OBJECT, WHATEVER api-docs SAYS ───────────────────
 * `TripWaypointResource` types `location` as a STRING. It is not: it arrives as
 * `{lat, lng, address, city, state, country, postal_code}`. Rendering it straight into
 * JSX is React error #31, and it is the exact trap `placeLabel`/`locationLabel` exist to
 * absorb — so every label here goes through `locationLabel`, never the raw field.
 *
 * The upside of the same discovery: a waypoint DOES carry its own coordinates, so a drop
 * is normally self-describing — identity, address and point all from one record.
 *
 * ── The route is the FALLBACK for geometry, not the source ────────────────────────
 * A waypoint whose `location` came back as a bare string (the shape api-docs describes,
 * and what some endpoints do send) has no point of its own. For those, `route.legs`
 * supplies it positionally: both lists describe the same journey in the same order, and a
 * route of N legs has exactly N+1 endpoints — `legs[0].start_location`, then every
 * `end_location` — i.e. one per waypoint. That join is only attempted when the two
 * lengths agree; when they do not, a waypoint without coordinates simply has none.
 *
 * With no usable waypoints at all, the manifest is built from the route's own endpoints
 * and labelled from `origin` / `stop_overs` / `destination`, which describe that same
 * sequence. Such drops carry no `id`, so they cannot be marked arrived — see
 * `ManifestPanel`, which gates the button on exactly that.
 *
 * ⚠️ A DROP WITH NO POINT IS DROPPED. Routing cannot use it, the map cannot draw it, and
 * a row on the sheet that the run does not include is worse than a shorter list — it
 * reads as a stop the driver still has to make.
 */

/** Every endpoint of the solved route, in order: leg 0's start, then each leg's end. */
function routePoints(trip) {
    const legs = trip?.route?.legs || []
    if (legs.length === 0) return []

    const points = [toPoint(legs[0]?.start_location)]
    for (const leg of legs) points.push(toPoint(leg?.end_location))
    return points
}

/** `origin → stop-overs → destination` as display strings — the fallback label source. */
function addressSequence(trip) {
    const stops = (trip?.stop_overs || []).map((s) => locationLabel(s?.location || s))
    return [locationLabel(trip?.origin), ...stops, locationLabel(trip?.destination)]
}

/**
 * @param {object} trip  a DriverTripResource
 * @returns {Array<{
 *   id: number|null, sequence: number, type: string, typeLabel: string,
 *   label: string, point: {lat: number, lng: number},
 *   isCompleted: boolean, arrivedAt: string|null, departedAt: string|null,
 * }>} the run, in driving order
 */
export function buildManifest(trip) {
    const waypoints = [...(trip?.waypoints || [])].sort(
        (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0),
    )
    const points = routePoints(trip)
    const addresses = addressSequence(trip)

    /*
     * WAYPOINTS FIRST — they are the record of the run, and each one normally carries its
     * own coordinates. The route is consulted only for a waypoint whose `location` came
     * back as a bare string, and only when the two lists agree on how many places there
     * are; a positional join across lists of different lengths would put a drop at another
     * drop's coordinates, which routes the driver to the wrong door without erroring.
     */
    if (waypoints.length > 0) {
        const aligned = waypoints.length === points.length
        const last = waypoints.length - 1

        const drops = waypoints
            .map((w, i) => {
                // ⚠️ `w.location` is an OBJECT — never rendered raw, never read for `.lat`
                // without `toPoint`, which is what coerces its string coordinates.
                const point = toPoint(w?.location) || (aligned ? points[i] : null)
                if (!point) return null

                return {
                    id: w?.id ?? null,
                    sequence: w?.sequence ?? i + 1,
                    type: w?.type || (i === 0 ? 'pickup' : (i === last ? 'dropoff' : 'stop_over')),
                    typeLabel: w?.type_label || (i === 0 ? 'Pick-up' : (i === last ? 'Drop-off' : `Stop ${i}`)),
                    label: locationLabel(w?.location) || addresses[i] || `Stop ${i + 1}`,
                    point,
                    /**
                     * DONE WITH, FOR ROUTING — which means DEPARTED, not merely arrived.
                     *
                     * ⚠️ `arrived_at` USED TO COUNT HERE AND THAT WAS TOO EARLY. A driver who
                     * has pulled up at a stop has not left it: the passenger may still be
                     * getting in, and the leg to the NEXT stop does not begin until they
                     * press Leave. Completing the drop on arrival made the map jump ahead and
                     * draw the following leg while the car was still standing at this one.
                     *
                     * ⚠️ `is_completed` STAYS, and it is what closes the DROP-OFF. The last
                     * stop is never departed — there is no Leave on it, the ride is completed
                     * instead — so a rule of `departed_at` alone would leave the final drop
                     * outstanding forever and the run would never empty.
                     */
                    isCompleted: !!(w?.is_completed || w?.departed_at),
                    arrivedAt: w?.arrived_at ?? null,
                    departedAt: w?.departed_at ?? null,
                }
            })
            .filter(Boolean)

        if (drops.length > 0) return drops
    }

    /*
     * NO USABLE WAYPOINTS — fall back to the route's own endpoints, labelled from the
     * trip's addresses. These drops have no `id`, so the panel's Arrived button disables
     * itself with a reason rather than posting to `/waypoints/null/arrive`.
     */
    if (points.length === 0) return []

    return points
        .map((point, i) => {
            if (!point) return null
            const last = points.length - 1

            return {
                id: null,
                sequence: i + 1,
                // The position says what the waypoint type would have: the first place is
                // the COLLECTION (the passenger gets in), the last is the DELIVERY, the
                // rest are stops on the way.
                type: i === 0 ? 'pickup' : (i === last ? 'dropoff' : 'stop_over'),
                typeLabel: i === 0 ? 'Pick-up' : (i === last ? 'Drop-off' : `Stop ${i}`),
                label: addresses[i] || `Stop ${i + 1}`,
                point,
                // Nothing to read completion from, so the whole run reads as outstanding.
                isCompleted: false,
                arrivedAt: null,
                departedAt: null,
            }
        })
        .filter(Boolean)
}

/**
 * The drop being driven to — the first one not yet reached.
 *
 * Returns -1 for a finished run, which callers read as "nothing left to route to" rather
 * than falling back to the last drop; routing a driver to a place they have already been
 * is the failure this avoids.
 */
export function activeDropIndex(drops) {
    return drops.findIndex((d) => !d.isCompleted)
}

/** The drops still to be made, active one first — exactly what gets routed and listed. */
export function remainingDrops(drops) {
    const i = activeDropIndex(drops)
    return i === -1 ? [] : drops.slice(i)
}

export default buildManifest
